export type PrescriptionTag = "Delivery" | "Pickup" | "Critical" | "Needs Review"

export type Prescription = {
  id: string
  code: string
  patientName: string
  medicationSummary: string
  tags: PrescriptionTag[]
  isNew?: boolean
  createdAgoLabel: string
  status: "ready" | "needsReview" | null
}

export const prescriptions: Prescription[] = [
  {
    id: "rx-001",
    code: "RX001",
    patientName: "Ahmed Salem",
    medicationSummary: "Amlodipine 5mg - 30 tablets",
    tags: ["Delivery"],
    isNew: true,
    createdAgoLabel: "1 min ago",
    status: null,
  },
  {
    id: "rx-002",
    code: "RX002",
    patientName: "Mohammed Al-Rashid",
    medicationSummary: "Metformin 850mg - 90 tablets",
    tags: ["Delivery", "Pickup"],
    createdAgoLabel: "2 days ago",
    status: "ready",
  },
  {
    id: "rx-003",
    code: "RX003",
    patientName: "Emma Davis",
    medicationSummary: "Lisinopril 10mg - 30 tablets",
    tags: ["Critical", "Needs Review"],
    createdAgoLabel: "2 days ago",
    status: "needsReview",
  },
  {
    id: "rx-004",
    code: "RX004",
    patientName: "Ali Hassan",
    medicationSummary: "Atorvastatin 20mg - 30 tablets",
    tags: ["Pickup"],
    isNew: true,
    createdAgoLabel: "3 days ago",
    status: null,
  },
  {
    id: "rx-005",
    code: "RX005",
    patientName: "Fatima Khalid",
    medicationSummary: "Simvastatin 40mg - 60 tablets",
    tags: ["Pickup"],
    isNew: true,
    createdAgoLabel: "3 days ago",
    status: null,
  },
]

export type PrescriptionDetails = {
  status: "Active" | "Paused" | "Completed"
  items: { name: string; quantity: string }[]
  instructions: string
  clinicalNotes: string
  medicinalInfo: {
    medication: string
    erxNumber: string
    dosageFrequency: string
    quantity: string
    refillsAuthorized: string
    prescribedDate: string
    expiryDate: string
    receivedAgo: string
  }
  patientInfo: {
    urn: string
    episodeNumber: string
    weight: string
    height: string
    bsa: string
  }
  clinicalInfo: {
    allergies: string[]
    mainDiagnosis: string
    secondaryDiagnosis: string
  }
  medications: {
    id: string
    name: string
    strength?: string
    form: string
    dose: string
    route: string
    frequency: string
    duration: string
    packSize: string
    qtyOrdered: string
  }[]
}

export const prescriptionDetailsById: Record<string, PrescriptionDetails> = {
  "rx-001": {
    status: "Active",
    items: [],
    instructions: "Take with food. Complete full course even if symptoms improve.",
    clinicalNotes:
      "Patient reported symptoms for 3 days. Started with sore throat, now has fever.",
    medicinalInfo: {
      medication: "Amoxicillin 500mg",
      erxNumber: "ERX2025001847",
      dosageFrequency: "500mg / 3 times daily",
      quantity: "21 tablets",
      refillsAuthorized: "0",
      prescribedDate: "Nov 19, 2025",
      expiryDate: "Nov 19, 2026",
      receivedAgo: "2 hours ago",
    },
    patientInfo: {
      urn: "10392147",
      episodeNumber: "ER0000405931",
      weight: "68 kg",
      height: "160 cm",
      bsa: "1.74",
    },
    clinicalInfo: {
      allergies: ["Eggs", "Cows Milk", "Fish"],
      mainDiagnosis: "A09 - Infectious gastroenteritis and colitis, unspecified",
      secondaryDiagnosis: "K21.9 - Gastro-esophageal reflux disease without esophagitis",
    },
    medications: [
      {
        id: "med-1",
        name: "Sodium chloride/Potassium citrate/Dextrose/Sucrose",
        form: "powder in sachet",
        dose: "1 powder in sachet",
        route: "Oral",
        frequency: "Twice Daily",
        duration: "For 6 Day(s)",
        packSize: "Box (10 powder in sachet)",
        qtyOrdered: "20 powder in sachet",
      },
      {
        id: "med-2",
        name: "Paracetamol",
        strength: "1,000 mg",
        form: "tablet",
        dose: "1 tablet",
        route: "Oral",
        frequency: "Every 8 Hours",
        duration: "For 5 Day(s)",
        packSize: "Blister Pack (16 tablet)",
        qtyOrdered: "16 tablet",
      },
    ],
  },
  "rx-002": {
    status: "Active",
    items: [],
    instructions: "Take with meals.",
    clinicalNotes: "Type 2 Diabetes management.",
    medicinalInfo: {
      medication: "Metformin 850mg",
      erxNumber: "ERX2025001848",
      dosageFrequency: "850mg / 3 times daily",
      quantity: "90 tablets",
      refillsAuthorized: "1",
      prescribedDate: "Nov 17, 2025",
      expiryDate: "Nov 17, 2026",
      receivedAgo: "2 days ago",
    },
    patientInfo: {
      urn: "10392148",
      episodeNumber: "ER0000405932",
      weight: "85 kg",
      height: "175 cm",
      bsa: "2.03",
    },
    clinicalInfo: {
      allergies: ["Penicillin"],
      mainDiagnosis: "E11 - Type 2 diabetes mellitus",
      secondaryDiagnosis: "I10 - Essential (primary) hypertension",
    },
    medications: [
      {
        id: "med-3",
        name: "Metformin",
        strength: "850 mg",
        form: "tablet",
        dose: "1 tablet",
        route: "Oral",
        frequency: "3 times daily",
        duration: "For 30 Day(s)",
        packSize: "Box (30 tablets)",
        qtyOrdered: "90 tablets",
      },
    ],
  },
  "rx-003": {
    status: "Active",
    items: [],
    instructions: "Take in the morning.",
    clinicalNotes: "Blood pressure monitoring required.",
    medicinalInfo: {
      medication: "Lisinopril 10mg",
      erxNumber: "ERX2025001849",
      dosageFrequency: "10mg / Once daily",
      quantity: "30 tablets",
      refillsAuthorized: "3",
      prescribedDate: "Nov 17, 2025",
      expiryDate: "Nov 17, 2026",
      receivedAgo: "2 days ago",
    },
    patientInfo: {
      urn: "10392149",
      episodeNumber: "ER0000405933",
      weight: "62 kg",
      height: "165 cm",
      bsa: "1.69",
    },
    clinicalInfo: {
      allergies: ["Sulfa drugs"],
      mainDiagnosis: "I10 - Essential (primary) hypertension",
      secondaryDiagnosis: "None",
    },
    medications: [
      {
        id: "med-4",
        name: "Lisinopril",
        strength: "10 mg",
        form: "tablet",
        dose: "1 tablet",
        route: "Oral",
        frequency: "Once daily",
        duration: "For 30 Day(s)",
        packSize: "Box (30 tablets)",
        qtyOrdered: "30 tablets",
      },
    ],
  },
  "rx-004": {
    status: "Active",
    items: [],
    instructions: "Take at night.",
    clinicalNotes: "Cholesterol management.",
    medicinalInfo: {
      medication: "Atorvastatin 20mg",
      erxNumber: "ERX2025001850",
      dosageFrequency: "20mg / Once daily",
      quantity: "30 tablets",
      refillsAuthorized: "2",
      prescribedDate: "Nov 16, 2025",
      expiryDate: "Nov 16, 2026",
      receivedAgo: "3 days ago",
    },
    patientInfo: {
      urn: "10392150",
      episodeNumber: "ER0000405934",
      weight: "78 kg",
      height: "172 cm",
      bsa: "1.92",
    },
    clinicalInfo: {
      allergies: ["None"],
      mainDiagnosis: "E78.0 - Pure hypercholesterolemia",
      secondaryDiagnosis: "None",
    },
    medications: [
      {
        id: "med-5",
        name: "Atorvastatin",
        strength: "20 mg",
        form: "tablet",
        dose: "1 tablet",
        route: "Oral",
        frequency: "Once daily",
        duration: "For 30 Day(s)",
        packSize: "Box (30 tablets)",
        qtyOrdered: "30 tablets",
      },
    ],
  },
  "rx-005": {
    status: "Active",
    items: [],
    instructions: "Take at night.",
    clinicalNotes: "Cholesterol management.",
    medicinalInfo: {
      medication: "Simvastatin 40mg",
      erxNumber: "ERX2025001851",
      dosageFrequency: "40mg / Once daily",
      quantity: "60 tablets",
      refillsAuthorized: "1",
      prescribedDate: "Nov 16, 2025",
      expiryDate: "Nov 16, 2026",
      receivedAgo: "3 days ago",
    },
    patientInfo: {
      urn: "10392151",
      episodeNumber: "ER0000405935",
      weight: "70 kg",
      height: "168 cm",
      bsa: "1.79",
    },
    clinicalInfo: {
      allergies: ["Peanuts"],
      mainDiagnosis: "E78.0 - Pure hypercholesterolemia",
      secondaryDiagnosis: "None",
    },
    medications: [
      {
        id: "med-6",
        name: "Simvastatin",
        strength: "40 mg",
        form: "tablet",
        dose: "1 tablet",
        route: "Oral",
        frequency: "Once daily",
        duration: "For 60 Day(s)",
        packSize: "Box (30 tablets)",
        qtyOrdered: "60 tablets",
      },
    ],
  },
}